import Radio from '@liveauctioneers/caterwaul-components/lib/Radio/Radio';
import Radios from '@liveauctioneers/caterwaul-components/lib/Radio/Radios';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    fetchBidderPreferencesIfNeeded,
    updateBidderPreferences,
} from '@/redux/modules/bidder/preferences/bidderPreferences.actions';
import {
    bidderPreferenceLoadingSelector,
    bidderPreferenceSelector,
} from '@/redux/modules/bidder/preferences/bidderPreferences.selectors';
import { setPreferredCurrencyCookie } from '@/redux/modules/cookies';

type Props = {
    className?: string;
    onSaveCallback?: () => void;
};

export const CurrencySelection = ({ className, onSaveCallback }: Props) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const bidderPreferences = useAppSelector(bidderPreferenceSelector);
    const bidderPreferenceLoading = useAppSelector(bidderPreferenceLoadingSelector);
    const [selectedCurrency, setSelectedCurrency] = useState(bidderPreferences.preferredCurrency);

    const onChange = useCallback((event) => {
        setSelectedCurrency(event.currentTarget.name);
    }, []);

    const onSave = useCallback(() => {
        dispatch(
            updateBidderPreferences({
                ...bidderPreferences,
                preferredCurrency: selectedCurrency,
            })
        );
        onSaveCallback?.();
        dispatch(setPreferredCurrencyCookie());
    }, [dispatch, bidderPreferences, onSaveCallback, selectedCurrency]);

    useEffect(() => {
        setSelectedCurrency(bidderPreferences.preferredCurrency);
    }, [bidderPreferences]);

    useEffect(() => {
        dispatch(fetchBidderPreferencesIfNeeded({}));
    });

    return (
        <section className={cn('max-w-[250px]', className)}>
            <Radios
                onChange={onChange}
                selectedName={selectedCurrency}
            >
                <Radio
                    className="mt-xs pb-xs"
                    label="GBP"
                    name="GBP"
                    testid="GBP"
                />
                <Radio
                    className="mt-xs pb-xs"
                    label="EURO"
                    name="EUR"
                    testid="EUR"
                />
                <Radio
                    className="mt-xs pb-xs"
                    label="USD"
                    name="USD"
                    testid="USD"
                />
                <Radio
                    className="mt-xs pb-xs"
                    label={<FormattedMessage id="preferredCurrencyConversion.noConversion" />}
                    name="N/A"
                />
            </Radios>
            <Button
                className="mt-md w-full"
                data-testid="savePreferancesButton"
                loading={bidderPreferenceLoading}
                onClick={onSave}
            >
                {formatMessage({ id: 'preferredCurrencyConversion.save' })}
            </Button>
        </section>
    );
};
